import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import LoadingScreen from "./LoadingScreen";

// Routes that don't require authentication
const PUBLIC_ROUTES = ["/", "/login", "/about", "/privacy", "/delete-account"];

// Check if a path is a public route (including sub-routes)
const isPublicPath = (path) => {
  // Exact matches
  if (PUBLIC_ROUTES.includes(path)) return true;
  // Sub-routes of /about
  if (path.startsWith("/about/")) return true;
  return false;
};

const AppLoader = ({ children }) => {
  const { authState } = useSelector((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialLoad = useRef(true);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    // Only do redirect logic when auth state is determined
    if (authState === "checking") {
      return;
    }

    const currentPath = location.pathname;
    const isPublicRoute = isPublicPath(currentPath);
    const isHomePage = currentPath === "/";
    const isLoginPage = currentPath === "/login";

    // Handle initial load redirects
    if (isInitialLoad.current) {
      isInitialLoad.current = false;

      if (authState === "authenticated") {
        // Authenticated user on home/login page -> redirect to dashboard
        if (isHomePage || isLoginPage) {
          navigate("/dashboard", { replace: true });
          return;
        }
      } else if (authState === "unauthenticated") {
        // Unauthenticated user on native app home page -> redirect to login
        if (isNative && isHomePage) {
          navigate("/login", { replace: true });
          return;
        }
        // Unauthenticated user on protected route -> redirect
        if (!isPublicRoute) {
          if (isNative) {
            navigate("/login", { replace: true });
          } else {
            navigate("/", { replace: true });
          }
          return;
        }
      }
      // Otherwise, stay on current page
      return;
    }

    // After initial load, only redirect unauthenticated users from protected routes
    if (authState === "unauthenticated" && !isPublicRoute) {
      if (isNative) {
        navigate("/login", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
    // Authenticated users can navigate freely after initial load
  }, [authState, location.pathname, navigate, isNative]);

  // Show loading screen while checking auth state
  if (authState === "checking") {
    return <LoadingScreen />;
  }

  // Show loading screen during initial redirect if authenticated user is on home/login page
  if (isInitialLoad.current && authState === "authenticated") {
    const isHomePage = location.pathname === "/";
    const isLoginPage = location.pathname === "/login";
    if (isHomePage || isLoginPage) {
      return <LoadingScreen />;
    }
  }

  // For unauthenticated users trying to access protected routes, show loading while redirecting
  const isPublicRoute = isPublicPath(location.pathname);
  if (authState === "unauthenticated" && !isPublicRoute) {
    return <LoadingScreen />;
  }

  return children;
};

export default AppLoader;
